/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/components/modular-form/custom/PhonePrefixSelectField.tsx
 * Release: 1.1.1
 * Descrizione: Select custom per prefissi telefonici internazionali con bandierine SVG e raggruppamento per continente
 */

import React, { useState, useRef, useEffect } from 'react';
import type { OptionConfig } from '../types';

interface PhonePrefixSelectFieldProps {
  value: string;
  onChange: (value: string) => void;
  options: OptionConfig[];
  disabled?: boolean;
  placeholder?: string;
  tabIndex?: number;
}

// Componente per renderizzare la bandiera SVG (come in CountrySelectField)
const FlagIcon: React.FC<{ countryCode: string; className?: string }> = ({ 
  countryCode, 
  className = "w-5 h-4" 
}) => {
  return (
    <img
      src={`https://flagcdn.com/${countryCode.toLowerCase()}.svg`}
      alt={`Bandiera ${countryCode}`}
      className={`${className} rounded-sm object-cover`}
      onError={(e) => {
        // Fallback se l'immagine non carica
        const target = e.target as HTMLImageElement;
        target.style.display = 'none';
      }}
    />
  );
};

export const PhonePrefixSelectField: React.FC<PhonePrefixSelectFieldProps> = ({
  value,
  onChange,
  options,
  disabled = false,
  placeholder = 'Seleziona prefisso',
  tabIndex
}) => {
  console.log(`🔧 [PhonePrefixSelectField] RENDERING:`, {
    value,
    options: options?.length,
    disabled,
    placeholder,
    DEBUG_TIMESTAMP: new Date().toISOString()
  });
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Funzione per calcolare la posizione del dropdown
  const calculateDropdownPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const position = {
        top: rect.bottom + 5, // 5px di spazio dal bottone
        left: rect.left,
        width: rect.width
      };
      setDropdownPosition(position);
    }
  };

  // Non impostare automaticamente il default - lascia che sia gestito dal form
  // Il valore di default viene gestito dal form stesso quando non ci sono dati esistenti

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calcola la posizione del dropdown quando si apre
  useEffect(() => {
    if (isOpen) {
      calculateDropdownPosition();
    }
  }, [isOpen]);

  // Listener per lo scroll che ricalcola la posizione del dropdown
  useEffect(() => {
    const handleScroll = () => {
      if (isOpen) {
        calculateDropdownPosition();
      }
    };

    // Aggiungi listener per scroll su window e su elementi scrollabili
    window.addEventListener('scroll', handleScroll, true);
    document.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  // Listener per il resize della finestra
  useEffect(() => {
    const handleResize = () => {
      if (isOpen) {
        calculateDropdownPosition();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen]);

  const selectedOption = options.find(opt => opt.value === value);

  // Raggruppa le opzioni per continente
  const groupedOptions = options.reduce((acc, opt) => {
    const group = opt.group || 'Altri';
    if (!acc[group]) acc[group] = [];
    acc[group].push(opt);
    return acc;
  }, {} as Record<string, OptionConfig[]>);

  // Filtra le opzioni in base alla ricerca
  const filteredGroups = Object.entries(groupedOptions).map(([group, opts]) => [
    group,
    opts.filter(opt =>
      opt.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      opt.value.includes(searchTerm)
    )
  ] as [string, OptionConfig[]]).filter(([, opts]) => opts.length > 0);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) setSearchTerm('');
    }
  };

  // Handler per gestire il focus e la navigazione tab
  const handleFocus = () => {
    // Focus handler per accessibilità
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleDropdown();
    }
  };


  return (
    <div className="relative w-full" ref={dropdownRef}>

              <button
          ref={buttonRef}
          type="button"
          tabIndex={tabIndex || 1}
          onClick={toggleDropdown}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          disabled={disabled}
        className={`w-full px-3 py-2 border rounded-lg bg-zinc-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 border-zinc-600 placeholder-zinc-400 transition-colors duration-200 flex items-center justify-between ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-zinc-600'}`}
      >
        <div className="flex items-center">
          {selectedOption ? (
            <>
              {selectedOption.flag && <FlagIcon countryCode={selectedOption.flag} className="w-5 h-4 mr-2" />}
              <span className="text-sm text-zinc-200">{selectedOption.label} ({selectedOption.value})</span>
            </>
          ) : (
            <span className="text-zinc-400">{placeholder}</span>
          )}
        </div>
        <svg className={`w-4 h-4 text-zinc-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
      </button>
      

      
      {isOpen && (
        <div 
          className="fixed z-[999999] bg-zinc-800 border border-zinc-600 rounded-lg shadow-lg max-h-60 overflow-auto"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            position: 'fixed',
            zIndex: 999999,
            backgroundColor: '#27272a',
            border: '1px solid #52525b',
            borderRadius: '8px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)',
            maxHeight: '240px',
            overflow: 'auto'
          }}
        >

          <input
            type="text"
            className="w-full px-3 py-2 bg-zinc-700 text-white border-b border-zinc-600 focus:outline-none"
            placeholder="Cerca nazione o prefisso..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            autoFocus
          />
          {filteredGroups.length === 0 ? (
            <div className="p-3 text-zinc-400 text-sm">Nessun risultato</div>
          ) : (
            filteredGroups.map(([group, opts]) => (
              <div key={group}>
                <div className="px-3 py-1 text-xs font-semibold text-zinc-400 bg-zinc-900 sticky top-0">{group}</div>
                {opts.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`w-full flex items-center px-3 py-2 hover:bg-zinc-700 transition-colors ${opt.value === value ? 'bg-zinc-700' : ''}`}
                    onClick={() => handleSelect(opt.value)}
                  >
                    {opt.flag && <FlagIcon countryCode={opt.flag} className="w-5 h-4 mr-2" />}
                    <span className="text-sm text-zinc-200">{opt.label}</span>
                    <span className="ml-auto text-xs text-zinc-400">{opt.value}</span>
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default PhonePrefixSelectField; 